/**
 * guestPurchases.js
 *
 * Tiny localStorage layer for tracking audio-tour purchases made without
 * an account. The server is the source of truth — this is just a fast
 * client-side index so we can:
 *   1. Show "Purchased" / "Play" badges instantly on page load.
 *   2. Add the Authorization-style `purchase_token` to /access requests.
 *   3. Display a "Restore on another device" CTA.
 *
 * Schema in localStorage["wf_guest_purchases"] is an array of:
 *   { tour_id: string, purchase_token: string, saved_at: ISO string }
 *
 * We never store payment data or email here — those live in the DB.
 */
const KEY = 'wf_guest_purchases';

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeAll(arr) {
  try {
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch {
    /* quota, private mode — ignore */
  }
}

export function getGuestPurchases() {
  return readAll();
}

export function getGuestTourIds() {
  return readAll().map((p) => p.tour_id);
}

export function getTokenForTour(tourId) {
  const match = readAll().find((p) => p.tour_id === tourId);
  return match ? match.purchase_token : null;
}

export function addGuestPurchase(tourId, purchaseToken) {
  if (!tourId || !purchaseToken) return;
  const all = readAll().filter((p) => p.tour_id !== tourId);
  all.push({
    tour_id: tourId,
    purchase_token: purchaseToken,
    saved_at: new Date().toISOString(),
  });
  writeAll(all);
}

export function removeGuestPurchase(tourId) {
  writeAll(readAll().filter((p) => p.tour_id !== tourId));
}

export function clearGuestPurchases() {
  writeAll([]);
}

/**
 * After a customer signs in, add this device's guest purchases to their
 * account. The server only links a purchase when shown its access token,
 * so this can't claim anyone else's tours. Failures are ignored — the guest
 * token keeps working on this device either way.
 */
export async function claimGuestPurchases(api, customerToken) {
  const purchases = readAll();
  await Promise.all(purchases.map((p) =>
    api.post(
      `/audio-tours/access/${encodeURIComponent(p.purchase_token)}/claim`,
      {},
      { headers: { Authorization: `Bearer ${customerToken}` } },
    ).catch(() => {})
  ));
}
